/**
 * ClaimPage — allows a user to claim ownership of a shop via a transfer token.
 * Route: /claim/:token
 * No AuthGate — handles authentication inline.
 */
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore, getAuthHeaders } from '../../stores/authStore';
import { apiUrl } from '../../config';
import './ShopDetailPage.css';

interface TransferInfo {
  shopId: string;
  shopName: string;
  shopAddress: string;
  shopCategory: string;
  expiresAt: string;
}

export default function ClaimPage() {
  const { t } = useTranslation('shops');
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const authToken = useAuthStore((s) => s.token);
  const setAuth = useAuthStore((s) => s.setAuth);

  const [info, setInfo] = useState<TransferInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [claimedShopId, setClaimedShopId] = useState<string | null>(null);
  const [restoring, setRestoring] = useState(true);

  // Restore session from localStorage (same logic as AuthGate but without redirect)
  useEffect(() => {
    if (authToken) {
      setRestoring(false);
      return;
    }
    const stored = localStorage.getItem('dukkan_token');
    if (stored) {
      fetch(apiUrl('/api/auth/me'), {
        headers: { Authorization: `Bearer ${stored}` },
      })
        .then((res) => {
          if (res.ok) return res.json();
          throw new Error('expired');
        })
        .then((u) => {
          setAuth(u, stored);
          setRestoring(false);
        })
        .catch(() => {
          localStorage.removeItem('dukkan_token');
          setRestoring(false);
        });
    } else {
      setRestoring(false);
    }
  }, []);

  // Fetch transfer info (no auth needed)
  useEffect(() => {
    if (!token) return;
    fetch(apiUrl(`/api/shops/transfer-info/${token}`))
      .then((res) => {
        if (res.status === 410) throw new Error(t('claim.linkExpired'));
        if (!res.ok) throw new Error(t('claim.invalidLink'));
        return res.json();
      })
      .then((data) => {
        setInfo(data);
        setLoading(false);
      })
      .catch((e) => {
        setError(e.message);
        setLoading(false);
      });
  }, [token]);

  function handleSignIn() {
    // Store current URL so LoginPage can redirect back after sign-in
    localStorage.setItem('dukkan_redirect_after_login', window.location.pathname);
    navigate('/login');
  }

  async function handleClaim() {
    if (!token) return;
    setClaiming(true);
    try {
      const res = await fetch(apiUrl(`/api/shops/claim/${token}`), {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.detail || t('claim.claimFailed', { status: res.status }));
      }
      const shop = await res.json();
      setClaimed(true);
      setClaimedShopId(shop.id);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setClaiming(false);
    }
  }

  if (restoring || loading) {
    return (
      <div className="shop-detail-page" style={{ textAlign: 'center', paddingTop: '4rem' }}>
        <p style={{ color: '#94a3b8' }}>{t('common:loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="shop-detail-page" style={{ textAlign: 'center', paddingTop: '4rem' }}>
        <p style={{ fontSize: '2rem' }}>⚠️</p>
        <p style={{ color: '#f87171', marginBottom: '1rem' }}>{error}</p>
        <button className="sd-btn" onClick={() => navigate('/shops')}>{t('claim.goToMyShops')}</button>
      </div>
    );
  }

  if (claimed) {
    return (
      <div className="shop-detail-page" style={{ textAlign: 'center', paddingTop: '4rem' }}>
        <p style={{ fontSize: '2rem' }}>✅</p>
        <h2 style={{ color: '#e2e8f0', margin: '0.5rem 0' }}>{t('claim.shopClaimed')}</h2>
        <p style={{ color: '#94a3b8', marginBottom: '1rem' }}>
          {t('claim.shopNowYours', { shopName: info?.shopName })}
        </p>
        <button
          className="sd-btn sd-btn-primary"
          onClick={() => navigate(claimedShopId ? `/shops/${claimedShopId}` : '/shops')}
        >
          {t('claim.openShop')}
        </button>
      </div>
    );
  }

  return (
    <div className="shop-detail-page" style={{ textAlign: 'center', paddingTop: '3rem' }}>
      <p style={{ fontSize: '2rem' }}>🔗</p>
      <h2 style={{ color: '#e2e8f0', margin: '0.5rem 0' }}>{t('claim.ownershipTransfer')}</h2>
      <p style={{ color: '#94a3b8', marginBottom: '0.25rem' }}>
        {t('claim.invitedToOwnership')}
      </p>
      <h3 style={{ color: '#f1f5f9', margin: '0.5rem 0' }}>{info?.shopName}</h3>
      {info?.shopAddress && (
        <p style={{ color: '#64748b', fontSize: '0.85rem', margin: 0 }}>{info.shopAddress}</p>
      )}

      <div style={{ marginTop: '1.5rem' }}>
        {user ? (
          <button
            className="sd-btn sd-btn-primary"
            onClick={handleClaim}
            disabled={claiming}
            style={{ fontSize: '1rem', padding: '0.6rem 1.5rem' }}
          >
            {claiming ? t('claim.claiming') : t('claim.claimThisShop')}
          </button>
        ) : (
          <button
            className="sd-btn sd-btn-primary"
            onClick={handleSignIn}
            style={{ fontSize: '1rem', padding: '0.6rem 1.5rem' }}
          >
            {t('claim.signInToClaim')}
          </button>
        )}
      </div>

      <p style={{ color: '#475569', fontSize: '0.75rem', marginTop: '1.5rem' }}>
        {t('claim.expires', { date: new Date(info?.expiresAt || '').toLocaleString() })}
      </p>
    </div>
  );
}
